import { faToggleOn, faToggleOff, faRefresh, faUser, faUserCircle, faBookOpen, faClock, faBuilding, faBuildingCircleCheck, faBuildingCircleExclamation } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import React, { useEffect } from 'react'
import { Card, Row, Col } from 'react-bootstrap';
import StatsCard from '../../component/Cards/StatsCard';
import StatsCardWithProgress from '../../component/Cards/StatsCardWithProgress';
import DonutChart, { ChartDataItem } from '../../component/Charts/DonutChart';
import { NoDataView } from './VenueWiseReport';

interface AttendanceOverViewProps {
    data: any;
    loading: boolean;
    autoRefresh: boolean;
    setAutoRefresh: (value: boolean) => void;
    getData: () => void;
    setNextRefreshIn: (seconds: number) => void;
    lastSynced: any;
    nextRefreshIn: any;
}

export default function BioMetricOverview({ data, loading, autoRefresh, setAutoRefresh, getData, setNextRefreshIn, lastSynced, nextRefreshIn }: AttendanceOverViewProps) {
    const [chartData, setChartData] = React.useState<ChartDataItem[]>([]);
    useEffect(() => {
        if(data && data !== null) {
            const totalCount = data?.totalCenters || 0;
            const presentCount = data?.biometricStartedCenters || 0;
            const absentCount = totalCount - presentCount;

            const newChartData: ChartDataItem[] = [
                {
                    category: "Biometric Started",
                    value: presentCount,
                    fill: "#004A2F"
                },
                {
                    category: "Biometric Not Started",
                    value: absentCount,
                    fill: "#6C946F"
                }
            ];

            setChartData(newChartData);
        }
    }, [data]);

    return (
        <>
            <Card
                className="mt-4 shadow"
                style={{
                    borderRadius: "16px",
                    border: "1px solid #e9ecef",
                }}
            >
                <Card.Body className="p-4">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                        <h5 className="text-secondary fw-bold mb-0" style={{ fontSize: '18px' }}>BioMetric Overview</h5>
                        <div className="d-flex align-items-center gap-3">
                            <div className="d-flex align-items-center">
                                <FontAwesomeIcon
                                    icon={autoRefresh ? faToggleOn : faToggleOff}
                                    className={`cursor-pointer me-2 ${autoRefresh ? 'text-success' : 'text-muted'}`}
                                    style={{ fontSize: '18px' }}
                                    onClick={() => {
                                        setAutoRefresh(!autoRefresh);
                                        if (!autoRefresh) {
                                            setNextRefreshIn(120);
                                        }
                                    }}
                                    title={`Auto-refresh ${autoRefresh ? 'enabled' : 'disabled'}`}
                                />
                                <small className="text-muted">Auto-refresh</small>
                            </div>

                            <FontAwesomeIcon
                                icon={faRefresh}
                                className={`cursor-pointer text-primary ${loading ? 'fa-spin' : ''}`}
                                onClick={() => {
                                    getData();
                                    if (autoRefresh) {
                                        setNextRefreshIn(120);
                                    }
                                }}
                                style={{ fontSize: '16px' }}
                                title="Refresh now"
                            />
                        </div>
                    </div>

                    <Row>
                        <Col md={6} className="">
                            {chartData.length > 0 && data ? (
                                <DonutChart
                                    data={chartData}
                                    centerText={{
                                        label: "Total Centers",
                                        value: data?.totalCenters || 0
                                    }}
                                    height={400}
                                    width={400}
                                    showTooltip={true}
                                    showLegend={true}
                                    showCalloutLabels={true}
                                />
                            ) : (
                                <NoDataView message="No attendance data available for the selected shift." />
                            )}
                        </Col>
                        <Col md={6} className="">
                            {
                                data ?
                                    (
                                        <Col>
                                            <Col md={12} className="mb-3">
                                                <StatsCard
                                                    title="Total Centers"
                                                    value={data?.totalCenters || 0}
                                                    icon={faBuilding}
                                                    iconColor="primary"
                                                    valueColor="primary"
                                                />
                                            </Col>
                                            <Col md={12} className="mb-3">
                                                <StatsCard
                                                    title="Biometric Started Centers"
                                                    value={data?.biometricStartedCenters || 0}
                                                    icon={faBuildingCircleCheck}
                                                    iconColor="success"
                                                    valueColor="success"
                                                />
                                            </Col>
                                            <Col md={12} className="mb-3">
                                                <StatsCardWithProgress
                                                    title="Biometric Centers Progress"
                                                    value={`${((data?.biometricStartedCenters || 0) / (data?.totalCenters || 1) * 100).toFixed(2)}%`}
                                                    progress={(data?.biometricStartedCenters || 0) / (data?.totalCenters || 1) * 100}
                                                    icon={faBuildingCircleExclamation}
                                                    iconColor="success"
                                                    valueColor="success"
                                                />
                                            </Col>
                                        </Col>
                                    )
                                    : <NoDataView message="No attendance data available for the selected shift." />
                            }
                        </Col>
                    </Row>

                    {lastSynced && (
                        <div className="mt-1 d-flex align-items-center justify-content-end">
                            <FontAwesomeIcon icon={faClock} className="text-muted me-2" style={{ fontSize: '12px' }} />
                            <small className="text-muted">
                                Last synced: {moment(lastSynced).format('DD MMM YYYY, hh:mm:ss')}
                                {autoRefresh && (
                                    <span className="ms-2">
                                        • Next refresh in: <span className="fw-bold text-primary">{formatCountdown(nextRefreshIn)}</span>
                                    </span>
                                )}
                            </small>
                        </div>
                    )}
                </Card.Body>
            </Card>
        </>
    )
}

const formatCountdown = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
};